import React, { useCallback, useEffect, useState, Fragment } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  EventAvailable as EventAvailableIcon,
  Payment as PaymentIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as AttachMoneyIcon,
  Money as MoneyIcon,
  FilterList as FilterListIcon,
  CalendarToday as CalendarTodayIcon,
  Info as InfoIcon,
  Place as PlaceIcon,
} from "@mui/icons-material";
import { format, isValid, isBefore, isAfter, addHours } from "date-fns";
import { pl } from "date-fns/locale";
import axios from "axios";
import { useAuthStore } from "../../store";

// Import split payment utility
const hasSplitPayment = (lesson) => {
  return lesson?.online_payment_amount != null && lesson?.on_site_payment_amount != null;
};

/*****************
 *  UTILS
 *****************/
const formatLessonDate = (lesson) => {
  const parsedDate = new Date(lesson.start);

  if (!isValid(parsedDate)) {
    return {
      day: "Invalid Date",
      date: "Invalid Date",
      start: "Invalid Time",
      end: "Invalid Time",
      fullDate: parsedDate,
    };
  }

  const day = format(parsedDate, "EEEE", { locale: pl });
  const date = format(parsedDate, "dd MMM yyyy", { locale: pl });
  const start = format(parsedDate, "HH:mm", { locale: pl });
  const end = format(
    new Date(parsedDate.getTime() + lesson.duration * 1000),
    "HH:mm",
    { locale: pl },
  );

  return { day, date, start, end, fullDate: parsedDate };
};

const getPaymentStatus = (lesson) => {
  if (lesson.is_free) {
    return { label: "Darmowe", color: "success", icon: <CheckCircleIcon /> };
  }
  if (lesson.payed) {
    return { label: "Opłacone", color: "success", icon: <CheckCircleIcon /> };
  }
  if (lesson.payment_on_site) {
    return { label: "Na miejscu", color: "info", icon: <MoneyIcon /> };
  }
  if (hasSplitPayment(lesson)) {
    return { label: "Zadatek", color: "secondary", icon: <AttachMoneyIcon /> };
  }
  return { label: "Nieopłacone", color: "warning", icon: <PaymentIcon /> };
};

const isLessonUpcoming = (lessonDate) => {
  const now = new Date();
  return isAfter(lessonDate, now);
};

/*****************
 *  DIALOGS
 *****************/
const AcceptRegulaminLabel = (
  <Typography variant="body2">
    Akceptuję{" "}
    <a
      href={`${process.env.PUBLIC_URL}/statute`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "#5c4033", fontWeight: 600 }}
    >
      Regulamin
    </a>
  </Typography>
);

const PayDialog = ({
  open,
  onClose,
  onConfirm,
  acceptRegulamin,
  setAcceptRegulamin,
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
    <DialogTitle sx={{ fontWeight: "bold", color: "#5c4033" }}>
      Czy chcesz zapłacić za zajęcia?
    </DialogTitle>

    <DialogContent dividers>
      <FormControlLabel
        control={
          <Checkbox
            checked={acceptRegulamin}
            onChange={(e) => setAcceptRegulamin(e.target.checked)}
            sx={{
              color: "#5c4033",
              "&.Mui-checked": {
                color: "#5c4033",
              },
            }}
          />
        }
        label={AcceptRegulaminLabel}
      />
    </DialogContent>

    <DialogActions sx={{ pb: 2, pr: 3 }}>
      <Button onClick={onClose} color="error" variant="outlined">
        Nie
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        disabled={!acceptRegulamin}
        sx={{
          backgroundColor: "#5c4033",
          "&:hover": {
            backgroundColor: "#4a3329",
          },
        }}
      >
        Tak
      </Button>
    </DialogActions>
  </Dialog>
);

const signOutQuestion = (target) => {
  if (target?.wholeDuo) return "Czy na pewno chcesz anulować całą rezerwację?";
  if (target?.userName) return `Czy na pewno chcesz wypisać ${target.userName}?`;
  return "Czy na pewno chcesz się wypisać z zajęć?";
};

const SignOutDialog = ({
  open,
  onClose,
  onConfirm,
  currentLesson,
  signingOut,
  target,
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
    <DialogTitle sx={{ fontWeight: "bold", color: "#5c4033" }}>
      {signOutQuestion(target)}
    </DialogTitle>

    <DialogContent dividers>
      {target?.wholeDuo && currentLesson && !currentLesson.is_free && !currentLesson.payment_on_site && (
        <Typography variant="body2">
          Jeśli rezerwacja była opłacona, dostaniesz karnet „Odrabianie duo/trio” na te zajęcia.
        </Typography>
      )}
      {target?.duoSpot && currentLesson && !currentLesson.is_free && !currentLesson.payment_on_site && (
        <Typography variant="body2">
          Jeśli miejsce było opłacone, dostaniesz karnet „Odrabianie miejsca duo/trio” na te zajęcia.
        </Typography>
      )}
      {!target?.wholeDuo && !target?.duoSpot && currentLesson &&
        !currentLesson.is_free &&
        !currentLesson.payment_on_site && (
          <Typography variant="body2">
            Zajęcia zostaną zwrócone do karnetu.
            <br />
            Jeśli zajęcia zostały kupione bez karnetu, zostaną zwrócone do
            karnetu "Odrabianie zajęć".
          </Typography>
        )}
    </DialogContent>

    <DialogActions sx={{ pb: 2, pr: 3 }}>
      <Button onClick={onClose} color="error" variant="outlined" disabled={signingOut}>
        Nie
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        disabled={signingOut}
        sx={{
          backgroundColor: "#5c4033",
          "&:hover": {
            backgroundColor: "#4a3329",
          },
        }}
      >
        {signingOut ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Tak"}
      </Button>
    </DialogActions>
  </Dialog>
);

/*****************
 *  LESSON CARD
 *****************/
const LessonCard = ({ lesson, onPay, onSignOut, signingOut, currentLessonId }) => {
  const { day, date, start, end, fullDate } = formatLessonDate(lesson);
  const paymentStatus = getPaymentStatus(lesson);
  const isUpcoming = isLessonUpcoming(fullDate);
  // Group bookings: a friend booked by someone else never pays and, on a
  // duo/trio, cannot cancel; the booker cancels a duo/trio only as a whole.
  const bookedBySomeoneElse = Boolean(lesson.booked_by);
  const group = lesson.group || [];
  const isParticipant = lesson.is_participant !== false;
  // A single duo/trio spot is the user's own seat: it behaves like a regular lesson.
  const wholeDuo = Boolean(lesson.is_duo) && !lesson.single_spot;
  const duoSpot = Boolean(lesson.is_duo) && Boolean(lesson.single_spot);
  const canSignOut = !hasSplitPayment(lesson) && isParticipant && !(wholeDuo && bookedBySomeoneElse);
  const amountDue = lesson.amount_due ?? null;
  const showPay = !bookedBySomeoneElse && !lesson.is_free && !lesson.payment_on_site &&
    (amountDue !== null ? amountDue > 0 : !lesson.payed);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#FAF3E0",
        border: "2px solid #D2B48C",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 16px rgba(92, 64, 51, 0.15)",
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Lesson Type Header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#5c4033",
              fontSize: { xs: "1rem", sm: "1.25rem" },
              minWidth: 0,
              overflowWrap: "anywhere",
            }}
          >
            {lesson.lesson_type.friendly_name}
          </Typography>
          <Chip
            icon={paymentStatus.icon}
            label={paymentStatus.label}
            color={paymentStatus.color}
            size="small"
            sx={{ fontWeight: 600, flexShrink: 0, maxWidth: "100%" }}
            data-testid="payment-status-chip"
          />
        </Box>

        <Divider sx={{ mb: 2, borderColor: "#D2B48C" }} />

        {/* Date and Time */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarTodayIcon sx={{ color: "#5c4033", fontSize: 20 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#5c4033" }}>
                {day}
              </Typography>
              <Typography variant="body2" sx={{ color: "#8B7355" }}>
                {date}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ScheduleIcon sx={{ color: "#5c4033", fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {start} – {end}
            </Typography>
          </Box>

          {/* Location */}
          {lesson.lesson_type?.location?.name && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PlaceIcon sx={{ color: "#5c4033", fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {lesson.lesson_type.location.name}
              </Typography>
            </Box>
          )}

          {/* Upcoming indicator */}
          {isUpcoming && (
            <Chip
              icon={<EventAvailableIcon />}
              label="Nadchodzące"
              size="small"
              sx={{
                backgroundColor: "#E3F2FD",
                color: "#1976d2",
                fontWeight: 600,
                alignSelf: "flex-start",
              }}
            />
          )}
        </Box>

        {bookedBySomeoneElse && (
          <Typography variant="body2" sx={{ mt: 2, color: "#5c4033" }} data-testid="booked-by">
            Zapisane przez: <strong>{lesson.booked_by.full_name}</strong>
          </Typography>
        )}

        {group.length > 0 && (
          <Box sx={{ mt: 2 }} data-testid="group-members">
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#5c4033" }}>
              {isParticipant ? "Razem z Tobą:" : "Zarezerwowane dla:"}
            </Typography>
            {group.map((member) => (
              <Box
                key={member.user_id}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 0.5, flexWrap: "wrap" }}
                data-testid="group-member"
              >
                <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                  {member.full_name}
                  {member.paid_with_own_pass && (
                    <Chip size="small" label="z własnego karnetu" sx={{ ml: 1 }} />
                  )}
                </Typography>
                {!wholeDuo && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => onSignOut(lesson.id, { userId: member.user_id, userName: member.full_name })}
                  >
                    Wypisz
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Split payment info */}
        {hasSplitPayment(lesson) && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              backgroundColor: "#FFF3E0",
              borderRadius: 1,
              border: "1px solid #FFB74D",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <InfoIcon sx={{ fontSize: 18, color: "#F57C00" }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#F57C00" }}>
                Zadatek
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: "#8B7355" }}>
              Wypisanie niedostępne dla zadatku
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ p: 2, pt: 0, flexDirection: "column", gap: 1 }}>
        {showPay && (
          <Button
            variant="contained"
            fullWidth
            startIcon={<PaymentIcon />}
            onClick={() => onPay(lesson.id)}
            sx={{
              backgroundColor: "#5c4033",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#4a3329",
              },
            }}
          >
            {group.length > 0 && amountDue ? `Opłać zajęcia (${amountDue} zł)` : "Opłać zajęcia"}
          </Button>
        )}

        {canSignOut && (
          <Button
            variant="outlined"
            fullWidth
            startIcon={<CancelIcon />}
            onClick={() => onSignOut(lesson.id, { wholeDuo, duoSpot })}
            disabled={signingOut && currentLessonId === lesson.id}
            sx={{
              color: "#d32f2f",
              borderColor: "#d32f2f",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#b71c1c",
                backgroundColor: "rgba(211, 47, 47, 0.04)",
              },
            }}
          >
            {signingOut && currentLessonId === lesson.id ? (
              <CircularProgress size={20} />
            ) : wholeDuo ? (
              "Anuluj rezerwację"
            ) : (
              "Wypisz się"
            )}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

/*****************
 *  EMPTY STATE
 *****************/
const EmptyState = () => (
  <Paper
    sx={{
      p: 6,
      textAlign: "center",
      backgroundColor: "#FAF3E0",
      border: "2px dashed #D2B48C",
    }}
  >
    <EventAvailableIcon sx={{ fontSize: 80, color: "#D2B48C", mb: 2 }} />
    <Typography variant="h6" sx={{ fontWeight: 600, color: "#5c4033", mb: 1 }}>
      Brak zaplanowanych zajęć
    </Typography>
    <Typography variant="body2" sx={{ color: "#8B7355" }}>
      Zapisz się na zajęcia z harmonogramu
    </Typography>
  </Paper>
);

/*****************
 *  MAIN COMPONENT
 *****************/
const LessonsTable = () => {
  const [lessons, setLessons] = useState([]);
  const [filteredLessons, setFilteredLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  // payment / sign‑out dialog states
  const [paymentLessonId, setPaymentLessonId] = useState(null);
  const [acceptRegulamin, setAcceptRegulamin] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [signOutConfirmationOpen, setSignOutConfirmationOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  // Who is being signed out: the user, a friend they booked, or a whole duo.
  const [signOutTarget, setSignOutTarget] = useState(null);

  // snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const { user, setLessons: updateLessonsInStore } = useAuthStore((state) => ({
    user: state.user,
    setLessons: state.setLessons,
  }));

  /*****************
   *  EFFECTS
   *****************/
  const fetchLessons = useCallback(async () => {
      const idToken = await user.getIdToken();

      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/user/me/lessons`,
          {
            headers: { Authorization: `Bearer ${idToken}` },
          },
        );

        // Sort lessons by date (newest first)
        const sortedLessons = data.objects.sort((a, b) => {
          return new Date(a.start) - new Date(b.start);
        });

        setLessons(sortedLessons);
        updateLessonsInStore(sortedLessons);
      } catch {
        setError("Failed to fetch lessons");
      } finally {
        setLoading(false);
      }
  }, [user, updateLessonsInStore]);

  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]);

  // Filter lessons based on selected filter
  useEffect(() => {
    const now = new Date();
    
    let filtered = [...lessons];
    
    if (filter === "upcoming") {
      filtered = lessons.filter((lesson) => {
        const lessonDate = new Date(lesson.start);
        return isAfter(lessonDate, now);
      });
    } else if (filter === "past") {
      filtered = lessons.filter((lesson) => {
        const lessonDate = new Date(lesson.start);
        return isBefore(lessonDate, now);
      });
    }
    
    setFilteredLessons(filtered);
  }, [lessons, filter]);

  /*****************
   *  HANDLERS – DIALOG OPEN/CLOSE
   *****************/
  const openPayDialog = (lessonId) => {
    setPaymentLessonId(lessonId);
    setConfirmationOpen(true);
  };

  const closePayDialog = () => {
    setConfirmationOpen(false);
    setAcceptRegulamin(false);
    setPaymentLessonId(null);
  };

  const openSignOutDialog = (lessonId, target = {}) => {
    setPaymentLessonId(lessonId);
    setSignOutTarget(target);
    setSignOutConfirmationOpen(true);
  };

  const closeSignOutDialog = () => {
    setSignOutConfirmationOpen(false);
    setPaymentLessonId(null);
    setSignOutTarget(null);
  };

  /*****************
   *  HANDLERS – BUSINESS OPERATIONS
   *****************/
  const handlePayment = async () => {
    if (!acceptRegulamin) {
      window.alert("Proszę zaakceptować Regulamin.");
      return;
    }

    const idToken = await user.getIdToken();

    try {
      const { data: redirectUrl } = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/payment/create_order`,
        { lesson_id: paymentLessonId },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );

      window.location.href = redirectUrl;
    } catch (err) {
      console.error("Payment failed", err);
      window.alert("Nie udało się dokonać płatności.");
    } finally {
      closePayDialog();
    }
  };

  const handleSignOut = async () => {
    if (!paymentLessonId) {
      setSnackbarMessage("Invalid lesson ID.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setSigningOut(true);
    const idToken = await user.getIdToken();

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/sign_me_out/`,
        { lesson_id: paymentLessonId, user_id: signOutTarget?.userId || null },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );

      if (response.status === 200 && (signOutTarget?.userId || lessons.find((l) => l.id === paymentLessonId)?.group?.length)) {
        setSnackbarMessage(
          signOutTarget?.userId ? `Wypisano ${signOutTarget.userName}.` : "Wypisano się z zajęć!"
        );
        setSnackbarSeverity("success");
        await fetchLessons();
      } else if (response.status === 200) {
        const lessonObj = lessons.find((l) => l.id === paymentLessonId);
        const wasPaid =
          lessonObj?.payed || lessonObj?.payment_on_site || lessonObj?.is_free;

        setSnackbarMessage(
          wasPaid
            ? "Wypisano się z zajęć!"
            : signOutTarget?.duoSpot
              ? `Wypisano się z zajęć!\nJeśli miejsce było opłacone, dostaniesz karnet "Odrabianie miejsca duo/trio".`
              : `Wypisano się z zajęć!\nZajęcia zostaną zwrócone do karnetu.\n` +
                `Jeśli zajęcia zostały kupione bez karnetu, zostaną zwrócone do karnetu \"Odrabianie zajęć\".`,
        );
        setSnackbarSeverity("success");

        // remove lesson locally
        const updated = lessons.filter((l) => l.id !== paymentLessonId);
        setLessons(updated);
        updateLessonsInStore(updated);
      }
    } catch (err) {
      const { response } = err;
      if (response?.status === 400 && response.data.detail === "24_hours_only") {
        setSnackbarMessage(
          "Można wypisać się z zajęć tylko 24 godziny przed ich rozpoczęciem.",
        );
      } else if (typeof response?.data?.detail === "string" && response.data.detail !== "24_hours_only") {
        setSnackbarMessage(response.data.detail);
      } else {
        console.error("Error signing out", err);
        setSnackbarMessage("Failed to sign out of the lesson.");
      }
      setSnackbarSeverity("error");
    } finally {
      setSigningOut(false);
      setSnackbarOpen(true);
      closeSignOutDialog();
    }
  };

  /*****************
   *  HANDLERS - FILTERS
   *****************/
  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  /*****************
   *  RENDER HELPERS
   *****************/
  const currentLesson = lessons.find((l) => l.id === paymentLessonId);

  /*****************
   *  RENDER – RETURN
   *****************/
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: "#5c4033" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Filters */}
      {lessons.length > 0 && (
        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#5c4033" }}>
            Moje Zajęcia
            <Chip
              label={filteredLessons.length}
              size="small"
              sx={{
                ml: 2,
                backgroundColor: "#5c4033",
                color: "#fff",
                fontWeight: 600,
              }}
            />
          </Typography>

          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={handleFilterChange}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                "&.Mui-selected": {
                  backgroundColor: "#5c4033",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#4a3329",
                  },
                },
              },
            }}
          >
            <ToggleButton value="all">
              Wszystkie ({lessons.length})
            </ToggleButton>
            <ToggleButton value="upcoming">
              Nadchodzące (
              {lessons.filter((l) => isLessonUpcoming(new Date(l.start))).length})
            </ToggleButton>
            <ToggleButton value="past">
              Przeszłe (
              {lessons.filter((l) => !isLessonUpcoming(new Date(l.start))).length})
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Lessons Grid or Empty State */}
      {filteredLessons.length === 0 ? (
        <EmptyState />
      ) : (
        <Grid container spacing={3}>
          {filteredLessons.map((lesson) => (
            <Grid item xs={12} sm={6} md={4} key={lesson.id}>
              <LessonCard
                lesson={lesson}
                onPay={openPayDialog}
                onSignOut={openSignOutDialog}
                signingOut={signingOut}
                currentLessonId={paymentLessonId}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialogs */}
      <PayDialog
        open={confirmationOpen}
        onClose={closePayDialog}
        onConfirm={handlePayment}
        acceptRegulamin={acceptRegulamin}
        setAcceptRegulamin={setAcceptRegulamin}
      />

      <SignOutDialog
        open={signOutConfirmationOpen}
        onClose={closeSignOutDialog}
        onConfirm={handleSignOut}
        currentLesson={currentLesson}
        signingOut={signingOut}
        target={signOutTarget}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage.split("\n").map((line, idx) => (
            <Fragment key={idx}>
              {line}
              <br />
            </Fragment>
          ))}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LessonsTable;
